import { authFetch } from './client'

/**
 * Browser `EventSource` is dead-simple but can't attach an `Authorization`
 * header — auth tokens have to come from a cookie or a query param. Since
 * Logto issues bearer tokens, we use `authFetch` (which already injects the
 * header) plus a hand-rolled SSE parser over `Response.body`.
 *
 * Usage:
 *
 *     const ctl = new AbortController()
 *     subscribeSSE(`${API_BASE_URL}/...`, {
 *       signal: ctl.signal,
 *       onEvent: (ev) => { ... },
 *       onError: (err) => { ... },
 *     })
 *     // later: ctl.abort()
 */
export interface SSEEvent {
  event: string
  data: string
}

export interface SubscribeOptions {
  signal: AbortSignal
  onEvent: (event: SSEEvent) => void
  onError?: (err: unknown) => void
  onOpen?: () => void
  onClose?: () => void
}

export async function subscribeSSE(
  url: string,
  opts: SubscribeOptions,
): Promise<void> {
  const { signal, onEvent, onError, onOpen, onClose } = opts
  try {
    const resp = await authFetch(url, {
      method: 'GET',
      headers: { Accept: 'text/event-stream' },
      signal,
    })
    if (!resp.ok || !resp.body) {
      throw new Error(`SSE stream failed: HTTP ${resp.status}`)
    }
    onOpen?.()

    const reader = resp.body.getReader()
    const decoder = new TextDecoder('utf-8')
    let buffer = ''
    let eventName = 'message'
    let dataLines: string[] = []

    const flush = () => {
      if (dataLines.length > 0) {
        onEvent({ event: eventName, data: dataLines.join('\n') })
      }
      eventName = 'message'
      dataLines = []
    }

    while (!signal.aborted) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      // SSE records are separated by blank lines. We split on \n and process
      // each line; an empty line dispatches the buffered event.
      let nlIdx = buffer.indexOf('\n')
      while (nlIdx !== -1) {
        const raw = buffer.slice(0, nlIdx).replace(/\r$/, '')
        buffer = buffer.slice(nlIdx + 1)
        if (raw === '') {
          flush()
        } else if (raw.startsWith(':')) {
          // SSE comment / heartbeat — ignore.
        } else if (raw.startsWith('event:')) {
          eventName = raw.slice('event:'.length).trim()
        } else if (raw.startsWith('data:')) {
          dataLines.push(raw.slice('data:'.length).replace(/^ /, ''))
        }
        nlIdx = buffer.indexOf('\n')
      }
    }
    flush()
    onClose?.()
  } catch (err) {
    if (signal.aborted) {
      onClose?.()
      return
    }
    onError?.(err)
  }
}
